import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';

const DROPDOWN_CLASS = 'quantity-selector--dropdown';
const EXPANDED_CLASS = 'quantity-selector--expanded';
const OPEN_CLASS = 'quantity-selector--dropdown-open';
const MENU_OPEN_CLASS = 'quantity-selector__menu--open';
const MENU_ABOVE_CLASS = 'quantity-selector__menu--above';
const MENU_GAP_PX = 4;
const MENU_VIEWPORT_PADDING_PX = 16;
const MENU_PREFERRED_MAX_HEIGHT_PX = 240;

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLElement} [dropdownView]
 * @property {HTMLButtonElement} [dropdownTrigger]
 * @property {HTMLUListElement} [dropdownMenu]
 * @property {HTMLElement} [triggerValue]
 * @property {HTMLElement} [expandedView]
 * @property {HTMLElement} [expandedInputSlot]
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /** @type {((event: Event) => void) | null} */
  #cartUpdateHandler = null;

  /** @type {((event: MouseEvent) => void) | null} */
  #documentClickHandler = null;

  /** @type {Comment | null} */
  #menuAnchor = null;

  /** @type {HTMLUListElement | null} */
  #dropdownMenuEl = null;

  /** @type {HTMLButtonElement | null} */
  #dropdownTriggerEl = null;

  /** @type {((event: Event) => void) | null} */
  #menuWheelHandler = null;

  /** @type {((event: Event) => void) | null} */
  #menuTouchMoveHandler = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.classList.contains(DROPDOWN_CLASS)) {
      queueMicrotask(() => this.#initDropdownMode());
      return;
    }

    if (this.classList.contains('quantity-selector--caret')) {
      this.addEventListener('click', this.#handleCaretLayoutClick);
    }

    if (this.#isCartLineSelector()) {
      this.#cartUpdateHandler = this.#handleCartUpdate;
      document.addEventListener(ThemeEvents.cartUpdate, this.#cartUpdateHandler);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.classList.contains(DROPDOWN_CLASS)) {
      this.#teardownDropdownMode();
      return;
    }

    if (this.classList.contains('quantity-selector--caret')) {
      this.removeEventListener('click', this.#handleCaretLayoutClick);
    }

    if (this.#cartUpdateHandler) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartUpdateHandler);
      this.#cartUpdateHandler = null;
    }
  }

  #isCartLineSelector() {
    const { quantityInput } = this.refs;
    return quantityInput instanceof HTMLInputElement && Boolean(quantityInput.dataset.cartLine);
  }

  #handleCartUpdate = () => {
    this.#setLoadingState(false);
  };

  #setLoadingState(isLoading) {
    if (!this.#isCartLineSelector()) return;

    this.classList.toggle('quantity-selector--loading', isLoading);
    this.toggleAttribute('aria-busy', isLoading);

    const { quantityInput } = this.refs;
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.disabled = isLoading;
    }

    const buttons = this.refs.quantityButtons;
    if (Array.isArray(buttons)) {
      for (const button of buttons) {
        if (button instanceof HTMLButtonElement) {
          button.disabled = isLoading;
        }
      }
    }
  }

  /**
   * @param {string} name
   * @returns {HTMLElement | null}
   */
  #getRef(name) {
    if (name === 'dropdownMenu' && this.#dropdownMenuEl) return this.#dropdownMenuEl;
    if (name === 'dropdownTrigger' && this.#dropdownTriggerEl) return this.#dropdownTriggerEl;

    const ref = this.refs[name];
    if (ref instanceof HTMLElement) return ref;

    const element = this.querySelector(`[ref="${name}"]`);
    return element instanceof HTMLElement ? element : null;
  }

  #initDropdownMode() {
    this.#dropdownMenuEl = this.querySelector('[ref="dropdownMenu"]');
    this.#dropdownTriggerEl = this.querySelector('[ref="dropdownTrigger"]');

    if (!(this.#dropdownMenuEl instanceof HTMLUListElement)) return;
    if (!(this.#dropdownTriggerEl instanceof HTMLButtonElement)) return;

    if (this.classList.contains(EXPANDED_CLASS)) {
      this.#switchToExpandedMode({ focus: false });
    }

    this.#dropdownTriggerEl.addEventListener('click', this.#handleDropdownTriggerClick);
    this.#dropdownMenuEl.addEventListener('click', this.#handleDropdownMenuClick);

    this.#menuWheelHandler = (event) => {
      event.stopPropagation();
    };
    this.#menuTouchMoveHandler = (event) => {
      event.stopPropagation();
    };
    this.#dropdownMenuEl.addEventListener('wheel', this.#menuWheelHandler, { passive: true });
    this.#dropdownMenuEl.addEventListener('touchmove', this.#menuTouchMoveHandler, { passive: true });

    this.#documentClickHandler = (event) => {
      if (!(event.target instanceof Element)) return;
      if (!this.classList.contains(OPEN_CLASS)) return;

      if (this.#dropdownMenuEl?.contains(event.target)) return;
      if (this.contains(event.target)) return;

      this.#closeDropdown();
    };

    document.addEventListener('mousedown', this.#documentClickHandler, true);
    window.addEventListener('resize', this.#handleViewportChange);
    window.visualViewport?.addEventListener('resize', this.#handleViewportChange);
  }

  #teardownDropdownMode() {
    this.#closeDropdown();

    this.#dropdownTriggerEl?.removeEventListener('click', this.#handleDropdownTriggerClick);
    this.#dropdownMenuEl?.removeEventListener('click', this.#handleDropdownMenuClick);

    if (this.#menuWheelHandler) {
      this.#dropdownMenuEl?.removeEventListener('wheel', this.#menuWheelHandler);
      this.#menuWheelHandler = null;
    }

    if (this.#menuTouchMoveHandler) {
      this.#dropdownMenuEl?.removeEventListener('touchmove', this.#menuTouchMoveHandler);
      this.#menuTouchMoveHandler = null;
    }

    if (this.#documentClickHandler) {
      document.removeEventListener('mousedown', this.#documentClickHandler, true);
      this.#documentClickHandler = null;
    }

    window.removeEventListener('resize', this.#handleViewportChange);
    window.visualViewport?.removeEventListener('resize', this.#handleViewportChange);
  }

  #handleViewportChange = () => {
    if (!this.classList.contains(OPEN_CLASS)) return;
    this.#positionDropdownMenu();
  };

  #handleDropdownTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.classList.contains(EXPANDED_CLASS)) return;

    if (this.classList.contains(OPEN_CLASS)) {
      this.#closeDropdown();
    } else {
      this.#openDropdown();
    }
  };

  #handleDropdownMenuClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const option = event.target.closest('[data-quantity]');
    if (!(option instanceof HTMLButtonElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const quantityValue = option.dataset.quantity;

    if (quantityValue === 'more') {
      this.#switchToExpandedMode({ focus: true });
      return;
    }

    const parsedQuantity = parseInt(quantityValue ?? '', 10);
    if (Number.isNaN(parsedQuantity)) return;

    this.#setDropdownQuantity(parsedQuantity);
    this.#closeDropdown();
    this.#onQuantityChange(event);
  };

  #openDropdown() {
    const dropdownMenu = this.#dropdownMenuEl;
    const dropdownTrigger = this.#dropdownTriggerEl;
    if (!dropdownMenu || !dropdownTrigger) return;

    if (!this.#menuAnchor) {
      this.#menuAnchor = document.createComment('quantity-selector-menu-anchor');
      dropdownMenu.parentNode?.insertBefore(this.#menuAnchor, dropdownMenu);
    }

    document.body.append(dropdownMenu);
    dropdownMenu.hidden = false;
    dropdownMenu.classList.add(MENU_OPEN_CLASS);
    this.#positionDropdownMenu();
    this.#scrollMenuToSelectedOption();

    this.classList.add(OPEN_CLASS);
    dropdownTrigger.setAttribute('aria-expanded', 'true');
  }

  #closeDropdown() {
    const dropdownMenu = this.#dropdownMenuEl;
    const dropdownTrigger = this.#dropdownTriggerEl;

    this.classList.remove(OPEN_CLASS);
    dropdownTrigger?.setAttribute('aria-expanded', 'false');

    if (!dropdownMenu) return;

    dropdownMenu.hidden = true;
    dropdownMenu.classList.remove(MENU_OPEN_CLASS, MENU_ABOVE_CLASS);
    dropdownMenu.style.position = '';
    dropdownMenu.style.top = '';
    dropdownMenu.style.bottom = '';
    dropdownMenu.style.left = '';
    dropdownMenu.style.width = '';
    dropdownMenu.style.maxHeight = '';
    dropdownMenu.style.overflowY = '';
    dropdownMenu.style.zIndex = '';

    if (this.#menuAnchor?.parentNode) {
      this.#menuAnchor.parentNode.insertBefore(dropdownMenu, this.#menuAnchor.nextSibling);
    }
  }

  #positionDropdownMenu() {
    const dropdownMenu = this.#dropdownMenuEl;
    const dropdownTrigger = this.#dropdownTriggerEl;
    if (!dropdownMenu || !dropdownTrigger) return;

    const rect = dropdownTrigger.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;

    dropdownMenu.style.position = 'fixed';
    dropdownMenu.style.left = `${rect.left}px`;
    dropdownMenu.style.width = `${rect.width}px`;
    dropdownMenu.style.zIndex = '9999';
    dropdownMenu.style.maxHeight = `${MENU_PREFERRED_MAX_HEIGHT_PX}px`;
    dropdownMenu.style.overflowY = 'auto';

    const spaceBelow = viewportHeight - rect.bottom - MENU_VIEWPORT_PADDING_PX;
    const spaceAbove = rect.top - MENU_VIEWPORT_PADDING_PX;
    const menuHeight = dropdownMenu.scrollHeight;
    const preferredHeight = Math.min(MENU_PREFERRED_MAX_HEIGHT_PX, menuHeight);
    const openBelow = spaceBelow >= preferredHeight || spaceBelow >= spaceAbove;

    dropdownMenu.classList.toggle(MENU_ABOVE_CLASS, !openBelow);

    if (openBelow) {
      const maxHeight = Math.max(Math.min(preferredHeight, spaceBelow - MENU_GAP_PX), 80);
      dropdownMenu.style.top = `${rect.bottom + MENU_GAP_PX}px`;
      dropdownMenu.style.bottom = 'auto';
      dropdownMenu.style.maxHeight = `${maxHeight}px`;
    } else {
      const maxHeight = Math.max(Math.min(preferredHeight, spaceAbove - MENU_GAP_PX), 80);
      dropdownMenu.style.top = 'auto';
      dropdownMenu.style.bottom = `${viewportHeight - rect.top + MENU_GAP_PX}px`;
      dropdownMenu.style.maxHeight = `${maxHeight}px`;
    }
  }

  #scrollMenuToSelectedOption() {
    const dropdownMenu = this.#dropdownMenuEl;
    if (!dropdownMenu) return;

    const selectedOption = dropdownMenu.querySelector('.quantity-selector__menu-option[aria-selected="true"]');
    if (!(selectedOption instanceof HTMLElement)) {
      dropdownMenu.scrollTop = 0;
      return;
    }

    const menuStyles = getComputedStyle(dropdownMenu);
    const menuPaddingTop = parseFloat(menuStyles.paddingTop) || 0;
    dropdownMenu.scrollTop = Math.max(0, selectedOption.offsetTop - menuPaddingTop);
  }

  /**
   * @param {number} quantity
   */
  #setDropdownQuantity(quantity) {
    const quantityInput = this.#getRef('quantityInput');
    const triggerValue = this.#getRef('triggerValue');
    if (!(quantityInput instanceof HTMLInputElement) || !triggerValue) return;

    quantityInput.value = String(quantity);
    triggerValue.textContent = String(quantity);
    this.#updateDropdownSelection(quantity);
  }

  /**
   * @param {number} quantity
   */
  #updateDropdownSelection(quantity) {
    const dropdownMenu = this.#dropdownMenuEl;
    if (!dropdownMenu) return;

    const options = dropdownMenu.querySelectorAll('.quantity-selector__menu-option[data-quantity]');
    for (const option of options) {
      if (!(option instanceof HTMLButtonElement)) continue;

      const isSelected =
        option.dataset.quantity !== 'more' && parseInt(option.dataset.quantity ?? '', 10) === quantity;
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    }
  }

  /**
   * @param {{ focus?: boolean }} options
   */
  #switchToExpandedMode({ focus = false }) {
    const quantityInput = this.#getRef('quantityInput');
    const dropdownView = this.#getRef('dropdownView');
    const expandedView = this.#getRef('expandedView');
    const expandedInputSlot = this.#getRef('expandedInputSlot');
    const triggerValue = this.#getRef('triggerValue');

    if (!(quantityInput instanceof HTMLInputElement) || !expandedView || !expandedInputSlot) return;

    const expandedMin = parseInt(this.dataset.expandedMin ?? '11', 10);
    const currentValue = parseInt(quantityInput.value, 10);
    const nextValue = Number.isNaN(currentValue) || currentValue <= 10 ? expandedMin : currentValue;

    this.#closeDropdown();
    this.classList.add(EXPANDED_CLASS);

    quantityInput.type = 'number';
    quantityInput.min = String(expandedMin);
    quantityInput.value = String(nextValue);
    quantityInput.classList.add('quantity-selector__form-input--expanded');
    quantityInput.setAttribute('aria-label', this.#dropdownTriggerEl?.getAttribute('aria-label') ?? 'Quantity');
    quantityInput.setAttribute('on:blur', '/setQuantity');
    quantityInput.setAttribute('on:focus', '/selectInputValue');

    if (dropdownView) dropdownView.hidden = true;
    expandedView.hidden = false;
    expandedInputSlot.append(quantityInput);

    if (triggerValue) triggerValue.textContent = String(nextValue);

    if (focus) {
      quantityInput.focus();
      quantityInput.select();
    }
  }

  #handleCaretLayoutClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('input[type="number"]')) return;

    event.preventDefault();
    event.stopPropagation();

    const { quantityInput } = this.refs;
    quantityInput?.focus();
    quantityInput?.select();
  };

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (this.classList.contains('quantity-selector--loading')) return;

    event.preventDefault();
    event.stopPropagation();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (this.classList.contains('quantity-selector--loading')) return;

    event.preventDefault();
    event.stopPropagation();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange(event);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (this.classList.contains('quantity-selector--loading')) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();

    const newValue = parseInt(quantityInput.value, 10);
    if (Number.isNaN(newValue)) return;

    if (this.classList.contains(DROPDOWN_CLASS) && !this.classList.contains(EXPANDED_CLASS)) {
      const { triggerValue } = this.refs;
      if (triggerValue) triggerValue.textContent = String(newValue);
      this.#updateDropdownSelection(newValue);
    }

    if (this.#isCartLineSelector()) {
      this.#setLoadingState(true);
    }

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
